import { createFileRoute, Link } from "@tanstack/react-router";
import { ArrowRight, PawPrint, GraduationCap, Stethoscope, UtensilsCrossed, Sparkles, Leaf, LifeBuoy } from "lucide-react";
import { SiteShell } from "@/project/components/site-shell";
import { initiatives } from "@/project/data/sample";

const ICONS: Record<string, typeof PawPrint> = {
  PawPrint, GraduationCap, Stethoscope, UtensilsCrossed, Sparkles, Leaf, LifeBuoy,
};

export const Route = createFileRoute("/initiatives/")({
  head: () => ({
    meta: [
      { title: "Initiatives — Daan Amrut Foundation" },
      { name: "description", content: "Seven initiatives across animal welfare, education, healthcare, food, women's empowerment, environment and disaster relief." },
      { property: "og:title", content: "Our Initiatives — Daan Amrut Foundation" },
      { property: "og:description", content: "Explore all seven initiatives run by Daan Amrut Foundation." },
    ],
  }),
  component: Page,
});

function Page() {
  return (
    <SiteShell>
      <section className="mx-auto max-w-7xl px-4 py-16 sm:px-6">
        <p className="text-xs font-semibold uppercase tracking-[0.18em] text-[var(--saffron-deep)]">All initiatives</p>
        <h1 className="mt-2 font-display text-4xl font-semibold text-[var(--maroon-deep)] sm:text-5xl">Seven fronts of seva</h1>
        <p className="mt-3 max-w-2xl text-foreground/70">
          From feeding rescued cows to teaching tribal children — pick the cause closest to your heart. Every rupee
          is tied to a specific programme with photographic and financial impact updates.
        </p>

        <div className="mt-10 grid gap-6 lg:grid-cols-2">
          {initiatives.map((i) => {
            const Icon = ICONS[i.icon] ?? PawPrint;
            return (
              <Link key={i.slug} to="/initiatives/$slug" params={{ slug: i.slug }}
                    className="group grid overflow-hidden rounded-3xl border border-border/60 bg-card shadow-[var(--shadow-soft)] transition hover:-translate-y-0.5 hover:shadow-[var(--shadow-card)] md:grid-cols-5">
                <div className="relative md:col-span-2 h-48 md:h-auto">
                  <img src={i.cover} alt="" className="h-full w-full object-cover group-hover:scale-105 transition-transform duration-500" />
                  <div className="absolute inset-0" style={{ background: `linear-gradient(180deg, transparent 30%, ${i.color} 100%)`, opacity: 0.85 }} />
                  <div className="absolute left-4 top-4 grid h-11 w-11 place-items-center rounded-xl bg-white/95 text-[var(--maroon-deep)]">
                    <Icon className="h-5 w-5" />
                  </div>
                  {!i.live && <span className="absolute right-4 top-4 rounded-full bg-white/95 px-2 py-1 text-[10px] font-semibold text-[var(--maroon-deep)]">Coming soon</span>}
                </div>
                <div className="p-6 md:col-span-3">
                  <p className="text-[11px] font-semibold uppercase tracking-wider" style={{ color: i.color }}>{i.tagline}</p>
                  <h2 className="mt-1 font-display text-2xl font-semibold text-[var(--maroon-deep)]">{i.name}</h2>
                  <p className="mt-2 text-sm text-foreground/70">{i.short}</p>
                  <div className="mt-4 grid grid-cols-3 gap-2">
                    {i.stats.map((s) => (
                      <div key={s.label} className="rounded-lg bg-[var(--cream-2)] p-2 text-center">
                        <p className="font-display text-sm font-semibold text-[var(--maroon-deep)]">{s.value}</p>
                        <p className="text-[10px] text-foreground/60">{s.label}</p>
                      </div>
                    ))}
                  </div>
                  <span className="mt-5 inline-flex items-center gap-1 text-sm font-semibold text-[var(--maroon)]">
                    Open {i.name} <ArrowRight className="h-4 w-4 group-hover:translate-x-0.5 transition-transform" />
                  </span>
                </div>
              </Link>
            );
          })}
        </div>
      </section>
    </SiteShell>
  );
}